
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Header from "../../Common/Layout/Header";
import { 
    RiMapPinLine, 
    RiCalendarLine, 
    RiUserLine, 
    RiEditLine, 
    RiAddLine, 
    RiDeleteBinLine, 
    RiArrowDownSLine, 
    RiArrowUpSLine,
    RiCheckLine,
    RiGlobalLine,
    RiHotelBedLine,
    RiRestaurantLine,
    RiSubtractLine,
    RiCloseLine,
    RiPlaneLine,
    RiEarthLine,
    RiMoonLine,
    RiArrowLeftLine,
    RiFileTextLine,
    RiMoneyDollarCircleLine,
    RiImageLine,
    RiUploadCloudLine,
    RiBriefcaseLine,
    RiFlightTakeoffLine,
    RiFlightLandLine,
    RiMagicLine
} from "react-icons/ri";

import destinationData from "../../Data/PackageDestinations.json";
import InteractiveCitySelector from "./InteractiveCitySelector";
import "./HolidayFlightPackage.css"; 

// --- Helpers ---
const addDays = (dateStr, days) => {
    if(!dateStr) return '';
    const result = new Date(dateStr);
    result.setDate(result.getDate() + parseInt(days));
    return result.toISOString().split('T')[0];
};

const formatDateShort = (dateStr) => {
    if(!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return ''; 
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const formatDateFull = (dateStr) => {
    if(!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

const defaultStartDate = new Date(Date.now() + 86400000).toISOString().split('T')[0];

const ManualPackageForm = ({ onGenerate }) => { 
    
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && !user) navigate("/");
    }, [user, loading, navigate]);

    // --- State ---
    
    // 1. Origin & Date
    const [origin, setOrigin] = useState({
        location: '',
        query: '',
        date: defaultStartDate
    });

    // 2. Return Destination
    const [returnDest, setReturnDest] = useState({
        location: '',
        query: ''
    });

    const [totalNights, setTotalNights] = useState(3);

    // 3. Destinations
    const [destinations, setDestinations] = useState([
        {
            id: Date.now(),
            destination: '', 
            query: '',       
            nights: 3,
            selectedCities: [] 
        }
    ]);

    // 4. Manual Specific Data
    const [formData, setFormData] = useState({
        packageName: '', 
        price: '',
        currency: 'INR',
        travelType: 'FAMILY',
        description: '',
        image: null,
        numAdults: 2,
        numChildren: 0,
        numInfants: 0,
        numRooms: 1,
        hotelCategory: '4 Star',
        mealType: 'MAP'
    });

    const [activeSearch, setActiveSearch] = useState(null); 
    const [searchResults, setSearchResults] = useState([]);
    const [isPassengerDropdownOpen, setPassengerDropdownOpen] = useState(false);
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [activeModalDestIndex, setActiveModalDestIndex] = useState(null); 
    const [showSummaryModal, setShowSummaryModal] = useState(false);

    const passengerRef = useRef(null);
    const fileInputRef = useRef(null);

    // --- Data Preparation ---
    const allDestinations = useMemo(() => {
        const list = [];
        if (destinationData) {
            destinationData.forEach(item => {
                if (item.Destination) {
                    list.push(item.Destination); 
                    if (item.Places && Array.isArray(item.Places)) {
                        item.Places.forEach(place => {
                            list.push(`${place}-${item.Destination}`);
                        });
                    }
                }
            });
        }
        return list;
    }, []);

    const returnDate = addDays(origin.date, totalNights);

    // --- Effects ---
    useEffect(() => {
        const sum = destinations.reduce((acc, dest) => acc + dest.nights, 0);
        if (sum > 0) setTotalNights(sum);
    }, [destinations]);

    // --- Handlers ---
    const handleSwitchToFlight = () => navigate('/holidayflightpackage');
    const handleSwitchToLand = () => navigate('/holidaylandpackage');
    const handleBackToHome = () => navigate('/holiday');

    const handleMainNightsChange = (delta) => {
        setTotalNights(prev => Math.max(1, prev + delta));
    };

    // --- Search & Select Handlers (Handling Origin, Return, and Dests) ---
    const handleSearch = (value, type, index = null) => {
        if (type === 'origin') {
            setOrigin(prev => ({ ...prev, query: value, location: value === '' ? '' : prev.location }));
        } else if (type === 'return') {
            setReturnDest(prev => ({ ...prev, query: value, location: value === '' ? '' : prev.location }));
        } else {
            const newDest = [...destinations];
            newDest[index].query = value;
            if (value === '') {
                newDest[index].destination = '';
                newDest[index].selectedCities = [];
            }
            setDestinations(newDest);
        }

        if (value.length > 0) {
            const lowerValue = value.toLowerCase();
            const filtered = allDestinations
                .filter(d => d.toLowerCase().includes(lowerValue))
                .slice(0, 7);
            setSearchResults(filtered);
            setActiveSearch(type === 'origin' ? 'origin' : type === 'return' ? 'return' : index);
        } else {
            setSearchResults([]);
            setActiveSearch(null);
        }
    };

    const handleSelect = (suggestion, type, index = null) => {
        const parts = suggestion.split('-');
        const countryName = parts.length > 1 ? parts[parts.length - 1] : suggestion;
        const cityName = parts.length > 1 ? parts[0] : null;

        if (type === 'origin') {
            setOrigin(prev => ({ ...prev, location: suggestion, query: suggestion }));
            // Auto-fill return if empty
            if (!returnDest.location) {
                setReturnDest({ location: suggestion, query: suggestion });
            }
        } else if (type === 'return') {
            setReturnDest({ location: suggestion, query: suggestion });
        } else {
            const newDest = [...destinations];
            const prevCountry = newDest[index].destination;
            
            if (prevCountry !== countryName) newDest[index].selectedCities = [];

            newDest[index].destination = countryName;
            newDest[index].query = suggestion;
            
            if (cityName) {
                if (!newDest[index].selectedCities.some(c => c.name === cityName)) {
                    newDest[index].selectedCities.push({
                        name: cityName,
                        nights: newDest[index].nights, 
                        country: countryName 
                    });
                }
            }
            setDestinations(newDest);
        }
        setSearchResults([]);
        setActiveSearch(null);
    };

    // --- Block Management ---
    const addDestinationBlock = () => {
        setDestinations([...destinations, {
            id: Date.now(),
            destination: '',
            query: '',
            nights: 3,
            selectedCities: [] 
        }]);
    };

    const removeDestinationBlock = (index) => {
        if (destinations.length > 1) {
            setDestinations(destinations.filter((_, i) => i !== index));
        }
    };

    const handleNightsChange = (index, delta) => {
        const newDest = [...destinations];
        newDest[index].nights = Math.max(1, newDest[index].nights + delta);
        if (newDest[index].selectedCities.length === 1) {
             newDest[index].selectedCities[0].nights = newDest[index].nights;
        }
        setDestinations(newDest);
    };
        const handleSelectDestination = (suggestion, index) => {
        const newDest = [...destinations];
        
        const parts = suggestion.split('-');
        const countryName = parts.length > 1 ? parts[parts.length - 1] : suggestion;
        const cityName = parts.length > 1 ? parts[0] : null;

        const prevCountry = newDest[index].destination;
        if (prevCountry !== countryName) {
            newDest[index].selectedCities = [];
        }

        newDest[index].destination = countryName;
        newDest[index].query = suggestion;
        
        if (cityName) {
            if (!newDest[index].selectedCities.some(c => c.name === cityName)) {
                newDest[index].selectedCities.push({
                    name: cityName,
                    nights: newDest[index].nights, 
                    country: countryName 
                });
            }
        }

        setDestinations(newDest);
        setActiveSearch(null);
        setSearchResults([]);
    };

    // --- City Modal Handlers ---
    const handleAddCity = (destIndex, cityName) => {
        const newDest = [...destinations];
        if (!newDest[destIndex].selectedCities.some(c => c.name === cityName)) {
            newDest[destIndex].selectedCities.push({
                name: cityName,
                nights: 1,
                country: newDest[destIndex].destination
            });
            setDestinations(newDest);
        }
    };

    const handleRemoveCity = (destIndex, cityName) => {
        const newDest = [...destinations];
        newDest[destIndex].selectedCities = newDest[destIndex].selectedCities.filter(c => c.name !== cityName);
        setDestinations(newDest);
    };

    const handleCityNightsChange = (destIndex, cityName, delta) => {
        const newDest = [...destinations];
        const cityIdx = newDest[destIndex].selectedCities.findIndex(c => c.name === cityName);
        if (cityIdx > -1) {
            const newNights = newDest[destIndex].selectedCities[cityIdx].nights + delta;
            if (newNights > 0) {
                newDest[destIndex].selectedCities[cityIdx].nights = newNights;
                setDestinations(newDest);
            }
        }
    };

    const handleCityReorder = (destIndex, newCityOrder) => {
        const newDest = [...destinations];
        newDest[destIndex].selectedCities = newCityOrder;
        setDestinations(newDest);
    };

    // --- Form & Passenger Handlers ---
    const handlePassengerChange = (type, delta) => {
        setFormData(prev => {
            const newValue = prev[type] + delta;
            if (type === 'numAdults' && newValue < 1) return prev;
            if (newValue < 0) return prev;
            return { ...prev, [type]: newValue };
        });
    };

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const imageUrl = URL.createObjectURL(file);
            setFormData(prev => ({ ...prev, image: imageUrl }));
        }
    };

    const handleGenerateClick = () => {
        if (!formData.packageName) return alert("Please enter a Package Name.");
        if (!origin.location) return alert("Please select a Starting Location.");
        if (!returnDest.location) return alert("Please select a Return Location.");
        if (destinations.some(d => !d.destination)) return alert("Please select all destinations.");
        setShowSummaryModal(true);
    };

    // --- FINAL PAYLOAD ---
    const handleFinalGenerate = () => {
        setIsGenerating(true);
        setShowSummaryModal(false);

        let currentDate = origin.date;
        let hotels = [];
        let flights = [];
        let previousLoc = origin.location;

        // 1. Build Flight Path & Hotels
        destinations.forEach(dest => {
            // Simulated Flight Data
            flights.push({
                departure: previousLoc,
                arrival: dest.destination,
                departureDate: currentDate
            });

            // Build Hotels
            let cityRunningDate = currentDate;
            dest.selectedCities.forEach(city => {
                hotels.push({
                    name: city.name,
                    nights: city.nights,
                    country: dest.destination,
                    checkInDate: cityRunningDate, 
                    cityCode: city.name.substring(0, 3).toUpperCase(), 
                    countryCode: 'XX' 
                });
                cityRunningDate = addDays(cityRunningDate, city.nights);
            });

            currentDate = addDays(currentDate, dest.nights);
            previousLoc = dest.destination;
        });

        // 2. Return Flight
        flights.push({
            departure: previousLoc,
            arrival: returnDest.location,
            departureDate: currentDate
        });

        const payload = {
            packageType: "Manual",
            journeyType: destinations.length > 1 ? "MultiCity" : "RoundTrip",
            origin: origin.location,
            returnDestination: returnDest.location,
            flights: flights, 
            hotels: hotels, 
            numberOfPeople: formData.numAdults + formData.numChildren + formData.numInfants,
            numberOfNights: totalNights,
            totalDays: totalNights + 1,
            ...formData, // Includes packageName, price, etc.
            userId: user?.id
        };

        if (onGenerate) {
            onGenerate(payload);
        } else {
            console.log("Manual Payload (Full Structure):", payload);
            setTimeout(() => setIsGenerating(false), 2000);
        }
    };

    let runningDate = origin.date;

    if (loading) return <div className="loading-screen">Loading...</div>;

    return (
        <div id="layout-wrapper">
            <Header User={user} />
            
            <div className="main-content" style={{ marginTop: '25px' }}>
                <div className="page-content">
                    <div className="container-fluid">
                         <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                            <button className="hfp-back-link" onClick={handleBackToHome}>
                                <RiArrowLeftLine /> Back to Selection
                            </button>
                        </div>
                        <div className="hfp-wrapper hfp-theme-manual">
                            
                            {/* --- HEADER --- */}
                            <div className="hfp-header">
                                <div className="hfp-header-left">
                                    <div className="hfp-header-icon-box manual">
                                        <RiEditLine />
                                    </div>
                                    <div>
                                        <h2 className="hfp-title">Manual Package Builder</h2>
                                        <p className="hfp-subtitle">Custom Itinerary Creator</p>
                                    </div>
                                </div>

                                <div className="hfp-header-right">
                                    <div className="hfp-passenger-wrapper" ref={passengerRef}>
                                        <button className="hfp-btn-passenger" onClick={() => setPassengerDropdownOpen(!isPassengerDropdownOpen)}>
                                            <RiUserLine className="me-2" /> {formData.numAdults} Adults, {formData.numChildren} Kids
                                            <div className="hfp-badge-plus"><RiAddLine /></div>
                                        </button>
                                        {isPassengerDropdownOpen && (
                                            <div className="hfp-passenger-dropdown">
                                                <h4 className="hfp-dropdown-title">Travelers</h4>
                                                {['numAdults', 'numChildren', 'numInfants'].map(key => (
                                                    <div key={key} className="hfp-p-row">
                                                        <div className="hfp-p-label">{key.replace('num', '')}</div>
                                                        <div className="hfp-p-ctrl">
                                                            <button onClick={() => handlePassengerChange(key, -1)}><RiSubtractLine/></button>
                                                            <span>{formData[key]}</span>
                                                            <button onClick={() => handlePassengerChange(key, 1)}><RiAddLine/></button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <button className="hfp-btn-outline-flight" onClick={handleSwitchToFlight}>
                                        <RiPlaneLine className="me-2" /> Switch to Flight
                                    </button>
                                    <button className="hfp-btn-outline-land" onClick={handleSwitchToLand}>
                                        <RiEarthLine className="me-2" /> Switch to Land
                                    </button>
                                </div>
                            </div>

                            <div className="hfp-body">
                                
                                {/* --- 1. PACKAGE DETAILS (Extra Column) --- */}
                                <div className="hfp-card hfp-main-block" style={{marginBottom: '1.5rem', background: '#f8fafc', borderColor: '#e2e8f0'}}>
                                    <div style={{display:'flex', gap:'1.5rem', alignItems:'flex-start'}}>
                                        <div style={{flex: 1}}>
                                            <label className="hfp-label"><RiFileTextLine/> Package Name</label>
                                            <input 
                                                type="text" className="hfp-input" placeholder="e.g. Dubai Summer Escape"
                                                value={formData.packageName}
                                                onChange={(e) => setFormData({...formData, packageName: e.target.value})}
                                            />
                                        </div>
                                        <div style={{width:'200px'}}>
                                            <label className="hfp-label"><RiBriefcaseLine/> Travel Type</label>
                                            <select className="hfp-select" value={formData.travelType} onChange={(e)=>setFormData({...formData, travelType: e.target.value})}>
                                                <option>FAMILY</option><option>HONEYMOON</option><option>FRIENDS</option><option>SOLO</option><option>CORPORATE</option>
                                            </select>
                                        </div>
                                        <div style={{width:'220px'}}>
                                            <label className="hfp-label"><RiMoneyDollarCircleLine/> Estimated Price</label>
                                            <div className="hfp-price-input">
                                                <select style={{width:'70px', border:'none', background:'transparent', fontWeight:600}} value={formData.currency} onChange={(e)=>setFormData({...formData, currency: e.target.value})}>
                                                    <option>INR</option><option>USD</option><option>AED</option>
                                                </select>
                                                <input type="number" style={{border:'none', width:'100%', outline:'none'}} placeholder="0" value={formData.price} onChange={(e)=>setFormData({...formData, price: e.target.value})} />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Description */}
                                    <div style={{marginTop:'1rem'}}>
                                        <label className="hfp-label"><RiFileTextLine/> Description</label>
                                        <input 
                                            type="text" className="hfp-input" placeholder="Short description of the trip..."
                                            value={formData.description}
                                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        />
                                    </div>
                                </div>

                                {/* --- 2. STARTING LOCATION & DURATION (MATCHES FLIGHT) --- */}
                                <div className="hfp-card hfp-main-block">
                                    <div className="hfp-main-grid">
                                        <div className="hfp-input-group flex-grow hfp-search-container">
                                            <label className="hfp-label"><RiFlightTakeoffLine/> Starting From</label>
                                            <input 
                                                type="text" className="hfp-input" placeholder="e.g. Hyderabad (HYD)"
                                                value={origin.query}
                                                onChange={(e) => handleSearch(e.target.value, 'origin')}
                                            />
                                            {activeSearch === 'origin' && searchResults.length > 0 && (
                                                <div className="hfp-dropdown-results">
                                                    {searchResults.map((item, idx) => (
                                                        <div key={idx} className="hfp-result-item" onClick={() => handleSelect(item, 'origin')}>
                                                            <strong>{item}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="hfp-input-group" style={{width: '200px'}}>
                                            <label className="hfp-label"><RiCalendarLine/> Start Date</label>
                                            <input 
                                                type="date" className="hfp-input"
                                                value={origin.date}
                                                onChange={(e) => setOrigin({...origin, date: e.target.value})}
                                                min={defaultStartDate}
                                            />
                                        </div>
                                        <div className="hfp-main-nights-box">
                                            <label className="hfp-label-light">TOTAL DURATION</label>
                                            <div className="hfp-counter-input dark-theme">
                                                <button onClick={() => handleMainNightsChange(-1)}><RiSubtractLine/></button>
                                                <span>{totalNights} Nights</span>
                                                <button onClick={() => handleMainNightsChange(1)}><RiAddLine/></button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* --- 3. DESTINATION BLOCKS --- */}
                                {destinations.map((dest, index) => {
                                    const destStartDate = runningDate;
                                    const destEndDate = addDays(destStartDate, dest.nights);
                                    runningDate = destEndDate; 
                                    let cityRunningDate = destStartDate;

                                    return (
                                        <div key={dest.id} className="hfp-destination-wrapper">
                                            <div className="hfp-card hfp-dest-card">
                                                <div className="hfp-dest-header">
                                                    <div className="hfp-input-group flex-grow hfp-search-container">
                                                        <label className="hfp-label"><RiMapPinLine /> Visiting Destination {index + 1} 
                                                            <span className="hfp-mini-date">{formatDateShort(destStartDate)} - {formatDateShort(destEndDate)}</span>
                                                        </label>
                                                        <input 
                                                            type="text" className="hfp-input" placeholder="e.g. Dubai-UAE"
                                                            value={dest.query}
                                                            onChange={(e) => handleSearch(e.target.value, 'dest', index)}
                                                        />
                                                        {activeSearch === index && searchResults.length > 0 && (
                                                            <div className="hfp-dropdown-results">
                                                                {searchResults.map((suggestion, idx) => (
                                                                    <div key={idx} className="hfp-result-item" onMouseDown={() => handleSelectDestination(suggestion, index)}>
                                                                        <strong>{suggestion}</strong>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="hfp-input-group" style={{width: '150px'}}>
                                                        <label className="hfp-label"><RiMoonLine /> Nights</label>
                                                        <div className="hfp-counter-input">
                                                            <button onClick={() => handleNightsChange(index, -1)}><RiSubtractLine/></button>
                                                            <span>{dest.nights}</span>
                                                            <button onClick={() => handleNightsChange(index, 1)}><RiAddLine/></button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {dest.destination && (
                                                    <div className="hfp-city-section">
                                                        <div className="hfp-city-header">
                                                            <span><RiGlobalLine/> Places in {dest.destination}</span>
                                                            <button className="hfp-link-btn manual" onClick={() => setActiveModalDestIndex(index)}>
                                                                <RiAddLine/> Add Places
                                                            </button>
                                                        </div>
                                                        <div className="hfp-city-tags">
                                                            {dest.selectedCities.map((city) => {
                                                                const thisCityDate = cityRunningDate;
                                                                cityRunningDate = addDays(cityRunningDate, city.nights);
                                                                return (
                                                                    <div key={city.name} className="hfp-city-pill manual">
                                                                        <span className="name">{city.name}</span>
                                                                        <span className="nights-badge-full manual">
                                                                            <span style={{opacity:0.7, marginRight:'5px', fontSize:'0.7rem'}}>{formatDateShort(thisCityDate)}</span>
                                                                            <RiHotelBedLine className="me-1"/> {city.nights}N Stay
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        {dest.selectedCities.length === 0 && <div className="hfp-empty-cities">No places added yet.</div>}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="hfp-dest-actions">
                                                {destinations.length > 1 && (
                                                    <button className="hfp-ext-btn delete" onClick={() => removeDestinationBlock(index)}><RiDeleteBinLine /></button>
                                                )}
                                                {index === destinations.length - 1 && (
                                                    <button className="hfp-ext-btn add manual" onClick={addDestinationBlock}><RiAddLine /></button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* --- 4. RETURN BLOCK (MATCHES FLIGHT) --- */}
                                <div className="hfp-card hfp-return-block">
                                    <div className="hfp-return-content">
                                        <div className="hfp-input-group flex-grow hfp-search-container">
                                            <label className="hfp-label"><RiFlightLandLine /> Return To</label>
                                            <input 
                                                type="text" className="hfp-input return-input" placeholder="e.g. Hyderabad (HYD)"
                                                value={returnDest.query}
                                                onChange={(e) => handleSearch(e.target.value, 'return')}
                                            />
                                            {activeSearch === 'return' && searchResults.length > 0 && (
                                                <div className="hfp-dropdown-results">
                                                    {searchResults.map((item, idx) => (
                                                        <div key={idx} className="hfp-result-item" onClick={() => handleSelect(item, 'return')}>
                                                            <strong>{item}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="hfp-return-date">
                                            <label className="hfp-label"><RiCalendarLine /> Return Date</label>
                                            <div className="date-val">{formatDateFull(returnDate)}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* --- 5. IMAGE UPLOAD & ADVANCED --- */}
                                <div className="hfp-advanced-section">
                                    <button className="hfp-toggle-advanced manual" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}>
                                        {showAdvancedOptions ? 'Hide' : 'Show'} Advanced Options
                                    </button>
                                    
                                    {showAdvancedOptions && (
                                        <div className="hfp-advanced-grid animate-fade-in">
                                            <div className="hfp-group" style={{gridColumn: 'span 2'}}>
                                                <label className="hfp-label"><RiImageLine/> Cover Image</label>
                                                <div className="hfp-upload-box" style={{height:'50px', minHeight:'auto', display:'flex', alignItems:'center', padding:'0 1rem'}} onClick={()=>fileInputRef.current?.click()}>
                                                    <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleImageUpload} />
                                                    <RiUploadCloudLine className="me-2"/> {formData.image ? "Image Selected" : "Click to Upload Image"}
                                                </div>
                                            </div>
                                            <div className="hfp-group"><label className="hfp-label">HOTEL CATEGORY</label><select className="hfp-select" value={formData.hotelCategory} onChange={(e)=>setFormData({...formData, hotelCategory: e.target.value})}><option>3 Star</option><option>4 Star</option><option>5 Star</option></select></div>
                                            <div className="hfp-group"><label className="hfp-label">MEAL TYPE</label><select className="hfp-select" value={formData.mealType} onChange={(e)=>setFormData({...formData, mealType: e.target.value})}><option value="CP">Breakfast</option><option value="MAP">Breakfast & Dinner</option><option value="AP">All Meals</option></select></div>
                                        </div>
                                    )}
                                </div>

                                <div className="hfp-footer">
                                    <button className="hfp-btn-generate manual" onClick={handleGenerateClick} disabled={isGenerating}>
                                        <RiMagicLine className="me-2" /> {isGenerating ? 'Generating...' : 'Review & Generate'}
                                    </button>
                                </div>

                            </div>
                        </div>

                        {/* --- MODALS (Reused Logic) --- */}
                        {activeModalDestIndex !== null && (
                            <div className="hfp-modal-overlay">
                                <div className="hfp-modal-container">
                                    <div className="hfp-modal-header">
                                        <h3 className="hfp-modal-title">Select Places in {destinations[activeModalDestIndex].destination}</h3>
                                        <button className="hfp-modal-close" onClick={() => setActiveModalDestIndex(null)}><RiCloseLine/></button>
                                    </div>
                                    <div className="hfp-modal-body">
                                        <InteractiveCitySelector 
                                            country={destinations[activeModalDestIndex].destination}
                                            cities={destinations[activeModalDestIndex].selectedCities}
                                            maxNights={destinations[activeModalDestIndex].nights}
                                            startDate={
                                                (() => {
                                                    let d = new Date(origin.date);
                                                    for(let i=0; i<activeModalDestIndex; i++){
                                                        d.setDate(d.getDate() + destinations[i].nights);
                                                    }
                                                    return d.toISOString().split('T')[0];
                                                })()
                                            }
                                            onAddCity={(name) => handleAddCity(activeModalDestIndex, name)}
                                            onRemoveCity={(name) => handleRemoveCity(activeModalDestIndex, name)}
                                            onNightsChange={(name, delta) => handleCityNightsChange(activeModalDestIndex, name, delta)}
                                            onReorder={(newOrder) => handleCityReorder(activeModalDestIndex, newOrder)}
                                        />
                                    </div>
                                    <div className="hfp-modal-footer">
                                        <button className="hfp-btn-primary manual" onClick={() => setActiveModalDestIndex(null)}>Done</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* --- SUMMARY MODAL --- */}
                        {showSummaryModal && (
                            <div className="hfp-modal-overlay">
                                <div className="hfp-modal-container" style={{maxWidth: '800px'}}>
                                    <div className="hfp-modal-header">
                                        <h3 className="hfp-modal-title"><RiCheckLine className="text-slate"/> Review Manual Package</h3>
                                        <button className="hfp-modal-close" onClick={() => setShowSummaryModal(false)}><RiCloseLine/></button>
                                    </div>
                                    <div className="hfp-modal-body">
                                        <h4 className="hfp-summary-title">Package Details</h4>
                                        <div className="hfp-summary-grid">
                                            <div className="hfp-summary-box"><div className="label">Name</div><div className="val">{formData.packageName}</div></div>
                                            <div className="hfp-summary-box"><div className="label">Travelers</div><div className="val">{formData.numAdults} Ad, {formData.numChildren} Ch</div></div>
                                            <div className="hfp-summary-box"><div className="label">Price</div><div className="val">{formData.currency} {formData.price || 'N/A'}</div></div>
                                        </div>

                                        <h4 className="hfp-summary-title mt-4">Itinerary Timeline</h4>
                                        <div className="hfp-summary-segments">
                                            {(() => {
                                                let summaryDateStr = origin.date;
                                                return destinations.map((dest, i) => {
                                                    const currentBlockDate = summaryDateStr;
                                                    const nextDateStr = addDays(summaryDateStr, dest.nights);
                                                    let cityRunnerStr = currentBlockDate;

                                                    const block = (
                                                        <div key={i} className="hfp-summary-card manual" style={{borderLeftColor: '#475569'}}>
                                                            <div className="hfp-sc-header">
                                                                <div className="hfp-sc-route">
                                                                    <RiMapPinLine className="me-2 text-slate"/>
                                                                    {dest.destination}
                                                                </div>
                                                                <div className="hfp-sc-dates">
                                                                    <RiCalendarLine className="me-1"/> 
                                                                    {formatDateFull(currentBlockDate)} <span style={{margin:'0 4px', opacity:0.5}}>to</span> {formatDateFull(nextDateStr)}
                                                                </div>
                                                            </div>
                                                            <div className="hfp-sc-cities">
                                                                {dest.selectedCities.map((c, idx) => {
                                                                    const checkInDisp = formatDateShort(cityRunnerStr);
                                                                    cityRunnerStr = addDays(cityRunnerStr, c.nights);
                                                                    return (
                                                                        <div key={idx} className="hfp-summary-tag manual">
                                                                            <RiHotelBedLine className="me-1"/> 
                                                                            {c.name} ({c.nights}N)
                                                                            <span style={{opacity: 0.6, borderLeft:'1px solid #ccc', paddingLeft:'6px', marginLeft:'6px', fontSize:'0.75rem', color: '#334155'}}>
                                                                                {checkInDisp}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                    summaryDateStr = nextDateStr;
                                                    return block;
                                                });
                                            })()}
                                        </div>
                                    </div>
                                    <div className="hfp-modal-footer">
                                        <button className="hfp-btn-outline me-2" onClick={() => setShowSummaryModal(false)}>Edit</button>
                                        <button className="hfp-btn-generate manual" onClick={handleFinalGenerate}>Confirm & Generate</button>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManualPackageForm;